import { useReducer, useEffect, useRef } from 'react';

import reduce from 'lodash/reduce';

import { IPolicyDriver } from '../../../interfaces';

export type TPolicyHealthCheckErrorCode =
  | 'invalid_drivers_licenses'
  | 'unverifiable_drivers'
  | 'drivers_ineligible'
  | 'invalid_update_after_renewal_offer'
  | 'unverifiable_vins'
  | 'vehicles_ineligible';

export type TPolicyHealthCheckError = {
  code: TPolicyHealthCheckErrorCode;
  fields: {
    drivers?: IPolicyDriver[];
    invalid_drivers_licenses?: {
      driving_license_number: string;
      driving_license_state: string;
    }[];
    driver_ids?: string[];
    drivers_licenses?: {
      driving_license_number: string;
      driving_license_state: string;
    }[];
    invalid_vins?: string[];
    vehicle_ids?: string[];
    vehicle_vins?: string[];
  };
};

export type TPolicyHealthCheckErrorState = {
  unverifiableDriversLicenses: {
    driving_license_number: string;
    driving_license_state: string;
  }[];
  ineligibleDriverIds: string[];
  ineligibleDriversLicenses: {
    driving_license_number: string;
    driving_license_state: string;
  }[];
  unverifiableVehicleVins: string[];
  ineligibleVehicleIds: string[];
  ineligibleVehicleVins: string[];
  ineligibleDrivers: IPolicyDriver[];
};

export type TPolicyHealthCheckErrorAction =
  | {
      type: TPolicyHealthCheckErrorCode;
      payload: { error: TPolicyHealthCheckError };
    }
  | {
      type: 'clear_errors';
    }
  | {
      type: undefined;
      payload: {
        error: TPolicyHealthCheckError;
      };
    };

export type TPolicyHealthCheckAnalytics = {
  reportNotFound: (data: { error_codes: string[] }) => void;
};

const initialHealthCheckErrorState = {
  unverifiableDriversLicenses: [],
  ineligibleDriverIds: [],
  ineligibleDriversLicenses: [],
  unverifiableVehicleVins: [],
  ineligibleVehicleIds: [],
  ineligibleVehicleVins: [],
  ineligibleDrivers: [],
};

function healthCheckErrorReducer(
  state: TPolicyHealthCheckErrorState,
  action: TPolicyHealthCheckErrorAction,
  onError: (error: any) => void
): TPolicyHealthCheckErrorState {
  switch (action.type) {
    case 'invalid_drivers_licenses':
      return {
        ...state,
        unverifiableDriversLicenses:
          action.payload.error.fields.invalid_drivers_licenses || [],
      };
    case 'unverifiable_drivers':
      return {
        ...state,
        ineligibleDriversLicenses:
          action.payload.error.fields.invalid_drivers_licenses || [],
      };
    case 'drivers_ineligible':
      return {
        ...state,
        ineligibleDriverIds: action.payload.error.fields.driver_ids || [],
        ineligibleDriversLicenses:
          action.payload.error.fields.drivers_licenses || [],
      };
    case 'invalid_update_after_renewal_offer':
      return {
        ...state,
        ineligibleDrivers:
          action.payload.error.fields.drivers?.map((driver, index) => {
            return { ...driver, id: index };
          }) || [],
      };
    case 'unverifiable_vins':
      return {
        ...state,
        unverifiableVehicleVins: action.payload.error.fields.invalid_vins || [],
      };
    case 'vehicles_ineligible':
      return {
        ...state,
        ineligibleVehicleIds: action.payload.error.fields.vehicle_ids || [],
        ineligibleVehicleVins: action.payload.error.fields.vehicle_vins || [],
      };
    case 'clear_errors':
      return initialHealthCheckErrorState;
    default:
      onError(action.payload.error);
      return initialHealthCheckErrorState;
  }
}

export type TUsePolicyHealthCheck = {
  onError: (error: any) => void;
  analytics?: TPolicyHealthCheckAnalytics;
  goToHealthCheck: () => void;
  navNext: () => void;
  cachedInitialHealthCheckErrorState?: TPolicyHealthCheckErrorState | null;
};
export function usePolicyHealthCheck({
  onError,
  analytics,
  goToHealthCheck,
  navNext,
  cachedInitialHealthCheckErrorState,
}: TUsePolicyHealthCheck) {
  const [healthCheckErrorDetails, dispatchHealthCheckErrorDetails] = useReducer(
    (
      state: TPolicyHealthCheckErrorState,
      action: TPolicyHealthCheckErrorAction
    ) => healthCheckErrorReducer(state, action, onError),
    cachedInitialHealthCheckErrorState || initialHealthCheckErrorState
  );

  const healthCheckErrorDetailsRef = useRef(healthCheckErrorDetails);
  useEffect(
    function handleHealthCheck() {
      if (healthCheckErrorDetailsRef.current !== healthCheckErrorDetails) {
        if (
          reduce(
            healthCheckErrorDetails,
            (bool, healthCheckErrorProp) =>
              !!healthCheckErrorProp.length || bool,
            false
          )
        ) {
          goToHealthCheck();
        } else {
          navNext();
        }
        healthCheckErrorDetailsRef.current = healthCheckErrorDetails;
      }
    },
    [healthCheckErrorDetails, goToHealthCheck, navNext]
  );

  function handlePolicyCheckoutError(error: TPolicyHealthCheckError) {
    analytics?.reportNotFound({ error_codes: [error.code] });
    dispatchHealthCheckErrorDetails({ type: error.code, payload: { error } });
  }

  function clearHealthCheckErrors() {
    dispatchHealthCheckErrorDetails({
      type: 'clear_errors',
    });
  }

  return {
    healthCheckErrorDetails,
    handlePolicyCheckoutError,
    clearHealthCheckErrors,
  };
}
